import React, { useCallback } from 'react';
import classnames from 'classnames';
import { Field, Formik, FormikErrors, FormikProps } from 'formik';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';

import { PageTitle } from 'ui/PageTitle';
import FluidButton from 'ui/FluidButton';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { StyledDateRangeInput } from 'containers/BookingList/StyledFilters';

import { useCurrentWidth } from 'effects/windowEffects';
import { makeSupplierServiceApi } from 'services/SupplierServiceApi/SupplierServiceApi';
import { useNotifications } from 'hooks/useNotifications';
import { useVccTransactionsCSV } from 'services/SupplierServiceApi/mutations/useSearchMutation';

const vccTransactionsCsvQueryKey = 'vcc-transactions-csv';

export interface IVccExportFormValues {
  startDate: string;
  endDate: string;
}

export const VccTransactionsExportPage: React.FC = () => {
  const { currentWidth } = useCurrentWidth();

  const initialValues: IVccExportFormValues = {
    startDate: '',
    endDate: '',
  };

  const validateVccExportForm = useCallback((values: IVccExportFormValues) => {
    const errors: FormikErrors<IVccExportFormValues> = {};
    if (!values.startDate) {
      errors.startDate = 'Field is required';
    }
    if (!values.endDate) {
      errors.endDate = 'Field is required';
    }
    return errors;
  }, []);

  const vccTransactionsCSV = useVccTransactionsCSV();
  const { showErrorNotification } = useNotifications();

  const handleFormSubmit = useCallback(async (values: IVccExportFormValues) => {
    try {
      const { data } = await vccTransactionsCSV.mutateAsync(values);
      const filename = `vcc-transactions-${values.startDate}-${values.endDate}.csv`;
      
      saveAs(URL.createObjectURL(data), filename);
    } catch(err) {      
      showErrorNotification('Failed to export to CSV');
    }
  }, []);

  return (
    <div className={classnames('task-create-page', { 'px-[20px]': currentWidth <= 1262, 'px-[80px]': currentWidth > 1262 })}>
      <PageTitle
        primaryText="Virtual Credit Card - Transactions"
      />
      {(
        <>
          <p className="task-list-filters-header font-pt-sans font-bold text-17px leading-22px">Filter Results</p>
          <Formik
            initialValues={initialValues}
            validate={validateVccExportForm}
            enableReinitialize={true}
            onSubmit={handleFormSubmit}
          >
            {(form: FormikProps<IVccExportFormValues>) => {
              return (
                <div className="flex flex-col gap-[15px]">
                  <div className="filter flex items-start gap-[20px]">
                    <Field name="startDate">
                      {({ field: { name, value }, form: { setFieldValue } }) => (
                        <div className="flex flex-col gap-[5px]">
                          <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs mb-[5px]">
                            Start Date *
                          </label>
                          <DatePickerStateProvider
                            defaultSelectedDates={[value]}
                            placeholder="SELECT DATE"
                            onDateChange={dates => {
                              const date = format(new Date(dates[0]), 'yyyy-MM-dd');
                              setFieldValue(name, date);
                            }}
                            isSingleDateSelection
                            render={(params: IDatePickerSateParams) => {
                              return (
                                <StyledDateRangeInput
                                  displayString={params.displayString}
                                  currentDate={params.datePickerCurrentDate}
                                  totalNights={params.totalNights}
                                  selectedDates={params.selectedDates}
                                  onDayClick={params.handleDayClick}
                                  onDayMouseOver={params.handleDateMouseOver}
                                  showDatePicker={params.showDatePicker}
                                  onNextClick={params.incrementDate}
                                  onPrevClick={params.decrementDate}
                                  calendarCount={1}
                                  customWidth={1}
                                  noPortal
                                  onMouseDown={params.toggleDatePicker}
                                  onClickOutside={params.hideDatePicker}
                                  showTotalNights={false}
                                  enablePastDates
                                  maxDate={new Date().toISOString()}
                                  invalid={!!(form.touched.startDate && form.errors.startDate)}
                                />
                              );
                            }}
                          />
                          {form.touched.startDate && form.errors.startDate && (
                            <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0 ">
                              {form.errors.startDate}
                            </p>
                          )}
                        </div>
                        )}
                    </Field>
                    <Field name="endDate">
                      {({ field: { name, value }, form: { setFieldValue } }) => (
                        <div className="flex flex-col gap-[5px]">
                          <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs mb-[5px]">
                            End Date *
                          </label>
                          <DatePickerStateProvider
                            defaultSelectedDates={[value]}
                            placeholder="SELECT DATE"
                            onDateChange={dates => {
                              const date = format(new Date(dates[0]), 'yyyy-MM-dd');
                              setFieldValue(name, date);
                            }}
                            isSingleDateSelection
                            render={(params: IDatePickerSateParams) => {
                              return (
                                <StyledDateRangeInput
                                  displayString={params.displayString}
                                  currentDate={params.datePickerCurrentDate}
                                  totalNights={params.totalNights}
                                  selectedDates={params.selectedDates}
                                  onDayClick={params.handleDayClick}
                                  onDayMouseOver={params.handleDateMouseOver}
                                  showDatePicker={params.showDatePicker}
                                  onNextClick={params.incrementDate}
                                  onPrevClick={params.decrementDate}
                                  calendarCount={1}
                                  customWidth={1}
                                  noPortal
                                  onMouseDown={params.toggleDatePicker}
                                  onClickOutside={params.hideDatePicker}
                                  showTotalNights={false}
                                  enablePastDates
                                  maxDate={new Date().toISOString()}
                                  invalid={!!(form.touched.endDate && form.errors.endDate)}
                                />
                              );
                            }}
                          />
                          {form.touched.endDate && form.errors.endDate && (
                            <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0 ">
                              {form.errors.endDate}
                            </p>
                          )}
                        </div>
                      )}
                    </Field>
                  </div>
                  
                  <div className="controls flex  justify-between items-center">
                    <FluidButton
                      type="primary"
                      submit
                      isLoading={vccTransactionsCSV.isPending}
                      onClick={form.submitForm}
                    >
                      Export CSV
                    </FluidButton>
                  </div>
                </div>
              );
            }}
          </Formik>
        </>
      )}

    </div>
  );
};
